import { Component, OnDestroy, OnInit, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DealService, DealReportDto, DealReportStatus } from '../../../services/api/deal';
import { DealReportSocketService } from '../../../services/api/deal-report-socket';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { EMPTY, Subscription, interval, merge, timer } from 'rxjs';
import { catchError, startWith, switchMap, take } from 'rxjs/operators';
import { DocNumberPipe } from '../../../services/request-number';

@Component({
  selector: 'app-view-deal-report',
  standalone: true,
  imports: [CommonModule, RouterModule, DocNumberPipe],
  templateUrl: './view-deal-report.html',
  styleUrl: './view-deal-report.scss'
})
export class ViewDealReportComponent implements OnInit, OnDestroy {
  dealId!: number;
  @ViewChild('reportContent') reportContent!: ElementRef;
  report: DealReportDto | null = null;
  loading = false;
  generating = false;
  generatingMessage: string | null = null;
  error: string | null = null;
  displayTitle: string | null = null;
  private generationFinished = false;
  /** id уже существующего отчёта на момент запуска перегенерации — чтобы poll-фолбэк
   *  не принял старый отчёт за только что сформированный. */
  private reportIdBeforeGeneration: number | null = null;

  // Translations
  titleText = 'Отчёт по сделке';
  backText = 'Назад к сделке';
  loadingText = 'Загрузка...';
  generateReportText = 'Сформировать отчёт';
  regenerateReportText = 'Перегенерировать';
  reportGeneralInfoText = 'Общая информация';
  reportTimelineText = 'Хронология';
  reportProsText = 'Плюсы';
  reportConsText = 'Минусы';
  reportRatingText = 'Оценка';
  reportConclusionText = 'Итог';
  downloadPdfText = 'Скачать PDF';
  reportEmptyForViewerText = 'Отчёт по сделке ещё не сформирован. Генерация доступна грузовладельцу.';
  reportNotAvailableUntilDeliveredText = 'Отчёт можно будет сформировать после завершения активности «Груз доставлен».';
  reportGenerationHintText = 'ИИ-отчёт по работе экспедитора можно сформировать на основе данных сделки и активностей.';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dealService: DealService,
    private dealReportSocket: DealReportSocketService,
    public languageService: LanguageService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  isCargoOwner = false;
  isCustomsBroker = false;
  canGenerateReport = false;

  ngOnInit(): void {
    const user = this.authService.getUser();
    const roleCode = user?.role?.code;
    this.isCargoOwner = roleCode === 'ROLE_CARGO_OWNER';
    const isFreightForwarder = roleCode === 'ROLE_FREIGHT_FORWARDER';
    this.isCustomsBroker = roleCode === 'ROLE_CUSTOMS_BROKER';
    if (!this.isCargoOwner && !isFreightForwarder && !this.isCustomsBroker) {
      this.router.navigate(['/']);
      return;
    }

    this.subscriptions.add(
      this.route.params.subscribe(params => {
        this.dealId = +params['id'];
        if (this.dealId) {
          this.updateTranslations();
          this.loadDealForGeneratePermission();
          this.loadReport();
        }
      })
    );

    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('deal.reportBlockTitle') || 'Отчёт по сделке';
    this.backText = this.languageService.translate('deal.back') || 'Назад к сделке';
    this.loadingText = this.languageService.translate('deal.loading') || 'Загрузка...';
    this.generateReportText = this.languageService.translate('deal.generateReport') || 'Сформировать отчёт';
    this.regenerateReportText = this.languageService.translate('deal.regenerateReport') || 'Перегенерировать';
    this.reportGeneralInfoText = this.languageService.translate('deal.reportGeneralInfo') || 'Общая информация';
    this.reportTimelineText = this.languageService.translate('deal.reportTimeline') || 'Хронология';
    this.reportProsText = this.languageService.translate('deal.reportPros') || 'Плюсы';
    this.reportConsText = this.languageService.translate('deal.reportCons') || 'Минусы';
    this.reportRatingText = this.languageService.translate('deal.reportRating') || 'Оценка';
    this.reportConclusionText = this.languageService.translate('deal.reportConclusion') || 'Итог';
    this.downloadPdfText = this.languageService.translate('deal.downloadReportPdf') || 'Скачать PDF';
    this.reportEmptyForViewerText = this.languageService.translate('deal.reportEmptyForViewer') || this.reportEmptyForViewerText;
    this.reportGenerationHintText = this.languageService.translate('deal.reportGenerationHint') || this.reportGenerationHintText;
    this.reportNotAvailableUntilDeliveredText =
      this.languageService.translate('deal.reportNotAvailableUntilDelivered') || this.reportNotAvailableUntilDeliveredText;
  }

  private loadDealForGeneratePermission(): void {
    this.dealService.getDealWithActivities(this.dealId).subscribe({
      next: (deal) => {
        const activities = deal.activities || [];
        this.canGenerateReport = activities.some(
          (a) => a.activityType?.code === 'DELIVERED' && a.completed
        );
        this.cdr.markForCheck();
      },
      error: () => {
        // Если не удалось загрузить сделку, просто не даём сформировать отчёт
        this.canGenerateReport = false;
        this.cdr.markForCheck();
      }
    });
  }

  loadReport(): void {
    this.loading = true;
    this.error = null;
    this.report = null;
    this.displayTitle = null;

    this.dealService.getReport(this.dealId).subscribe({
      next: (report) => {
        this.report = report ?? null;
        this.displayTitle = this.buildTitle();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.error = err.error?.message || 'Ошибка при загрузке отчёта';
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  generateReport(): void {
    if (this.generating || !this.isCargoOwner || !this.canGenerateReport) return;
    this.generating = true;
    this.generatingMessage = null;
    this.error = null;
    this.reportIdBeforeGeneration = this.report?.id ?? null;

    this.dealService.generateReport(this.dealId).subscribe({
      next: () => {
        // 202 Accepted — генерация идёт асинхронно на бэкенде, слушаем WS/poll дальше.
        this.watchGeneration();
      },
      error: (err) => {
        this.error = err.error?.message || 'Ошибка при формировании отчёта';
        this.generating = false;
        this.cdr.markForCheck();
      }
    });
  }

  /** Слушает прогресс генерации через WebSocket (с фолбэком на poll GET .../report). */
  private watchGeneration(): void {
    this.generationFinished = false;

    const ws$ = this.dealReportSocket.watch(this.dealId).pipe(
      catchError(err => {
        console.warn('Deal report websocket unavailable, using polling', err);
        return EMPTY;
      })
    );

    const poll$ = interval(3000).pipe(
      startWith(0),
      switchMap(() => this.dealService.getReport(this.dealId).pipe(
        catchError(() => EMPTY)
      ))
    );

    this.subscriptions.add(
      merge(ws$, poll$).subscribe(payload => this.handleGenerationUpdate(payload))
    );

    // Предохранитель: если ни WS, ни поллинг ничего не дали за разумное время — снимаем спиннер.
    this.subscriptions.add(
      timer(5 * 60 * 1000).pipe(take(1)).subscribe(() => {
        if (this.generationFinished) return;
        this.generationFinished = true;
        this.generating = false;
        this.error = this.error || 'Формирование отчёта занимает слишком много времени. Попробуйте ещё раз.';
        this.cdr.markForCheck();
      })
    );
  }

  /** payload — либо DealReportStatus (WS), либо DealReportDto|null (poll-фолбэк на GET .../report). */
  private handleGenerationUpdate(payload: DealReportStatus | DealReportDto | null): void {
    if (this.generationFinished || !payload) return;

    // Poll-ответ: DealReportDto напрямую (у него нет поля status). Пропускаем, если это
    // тот же отчёт, что уже был до запуска генерации (иначе при «Перегенерировать» poll
    // мгновенно принял бы старый отчёт за новый).
    if (!('status' in payload)) {
      const polled = payload as DealReportDto;
      if (polled.id === this.reportIdBeforeGeneration) return;
      this.generationFinished = true;
      this.report = polled;
      this.displayTitle = this.buildTitle();
      this.generating = false;
      this.generatingMessage = null;
      this.cdr.markForCheck();
      return;
    }

    const status = payload as DealReportStatus;
    if (status.message) {
      this.generatingMessage = status.message;
    }

    if (status.status === 'DONE') {
      this.generationFinished = true;
      this.report = status.report ?? this.report;
      this.displayTitle = this.buildTitle();
      this.generating = false;
      this.generatingMessage = null;
      this.cdr.markForCheck();
      return;
    }

    if (status.status === 'ERROR') {
      this.generationFinished = true;
      this.error = status.error || status.message || 'Ошибка при формировании отчёта';
      this.generating = false;
      this.generatingMessage = null;
      this.cdr.markForCheck();
      return;
    }

    this.cdr.markForCheck();
  }

  private buildTitle(): string | null {
    const c = this.report?.content;
    if (!c) {
      return null;
    }
    const lang = this.languageService.getCurrentLanguageValue();
    const raw = lang === 'en' ? (c.titleEn || c.titleRu) : (c.titleRu || c.titleEn);
    return raw || null;
  }

  downloadPdf(): void {
    if (!this.report?.content || !this.reportContent) {
      return;
    }
    const element = this.reportContent.nativeElement as HTMLElement;
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `deal-${this.dealId}-report.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const h2p = (window as any).html2pdf;
    if (typeof h2p === 'function') {
      h2p().set(opt).from(element).save();
    }
  }

  goBack(): void {
    const base = this.isCargoOwner ? '/cargo-owner/deal' : this.isCustomsBroker ? '/customs-broker/deal' : '/freight-forwarder/deal';
    this.router.navigate([base, this.dealId]);
  }
}
